import { useCallback, useEffect, useState } from "react";
import { Control, FieldArrayWithId, FieldErrors, useFieldArray, UseFormSetValue, useWatch } from "react-hook-form";
import { CustomTypeSectionWriteModel, CustomTypeWriteModel } from "../../api/CustomTypes/CustomTypes";
import { ToggleFormField } from "../../common/formfields/ToggleFormField";
import { OverlayInfo } from "../../common/misc/OverlayInfo/OverlayInfo";
import { useDynamicHeight } from "./hooks/useDynamicHeight";
import * as yup from "yup";
import styles from "./CustomTypeBuilder.module.css";
import { GridLayoutElementWrapper, NestedErrorContainer } from "./CustomTypeRenderUtils";
import { useResizeDetector } from "react-resize-detector";
import GridLayout from "react-grid-layout";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { CustomFieldFormCreateModal, useCustomFieldFormSchema } from "../CustomFields/CustomFieldsForm";
import { customFieldToCustomFieldForm } from "../CustomFields/CustomFieldUtils";
import { CustomField } from "../../api/CustomFields/CustomFields";
import { RenderCustomTypeSectionCustomField } from "./CustomTypeDetail";
import { InputFormFieldControlled } from "../../common/formfields/InputFormFieldControlled";

export const useCustomSectionFormSchema = ({
  disableCustomFieldNameCheck,
}: {
  disableCustomFieldNameCheck?: boolean;
}) => {
  const CustomFieldFormSchema = useCustomFieldFormSchema({ disableNameCheck: disableCustomFieldNameCheck });

  const CustomSectionFormSchema = yup.object().shape({
    name: yup.string().when("isHeadless", ([isHeadless], field) => {
      return !!isHeadless
        ? field.nullable().optional()
        : field.required("A section name is required").typeError("A section name is required");
    }),
    customFields: yup
      .array()
      .of(CustomFieldFormSchema)
      .min(1, "At least one custom field is required")
      .typeError("At least one custom field is required"),
  });
  return CustomSectionFormSchema;
};

interface CustomTypeSectionBuilderProps {
  control: Control<CustomTypeWriteModel, any, CustomTypeWriteModel>;
  setValue: UseFormSetValue<CustomTypeWriteModel>;
  updateSectionByElementId: (elementId: string, data: Partial<CustomTypeSectionWriteModel>) => void;
  sectionIndex: number;
  elementId: string;
  removeSection: (elementId: string) => void;
  nodeHeightCallback: (uuid: string, height: number) => void;
  disabled?: boolean;
  disabledRemove?: boolean;
  errors?: FieldErrors<FieldArrayWithId<CustomTypeWriteModel, "sections", "elementId">>;
}

interface LayoutState {
  [elementId: string]: {
    field: FieldArrayWithId<
      FieldArrayWithId<CustomTypeWriteModel, "sections", "elementId">,
      "customFields",
      "elementId"
    >;
    layout: GridLayout.Layout;
  };
}

export const CustomTypeSectionBuilder = ({
  control,
  setValue,
  updateSectionByElementId,
  sectionIndex,
  elementId,
  removeSection,
  nodeHeightCallback,
  disabled,
  disabledRemove,
  errors,
}: CustomTypeSectionBuilderProps) => {
  const { ref } = useDynamicHeight({
    uuid: elementId,
    nodeHeightCallback: nodeHeightCallback,
    gridRowHeight: 1,
  });

  const { ref: internalRef, width } = useResizeDetector();

  const { fields, insert, replace, remove } = useFieldArray({
    control,
    name: `sections.${sectionIndex}.customFields`,
    keyName: "elementId",
  });

  const [layout, setLayout] = useState<LayoutState>(
    Object.fromEntries(
      Object.entries(fields).map(([k, v]) => [
        v.elementId,
        { field: v, layout: { i: v.elementId, x: 0, y: 0, w: 1, h: 6 } },
      ])
    )
  );

  useEffect(() => {
    setLayout((prev) =>
      Object.fromEntries(
        Object.entries(fields).map(([k, v]) => [
          v.elementId,
          { field: v, layout: { i: v.elementId, x: 0, y: 0, w: 1, h: prev?.[v.elementId]?.layout?.h || 6 } },
        ])
      )
    );
    updateSectionByElementId(elementId, { customFields: fields });
  }, [elementId, fields, sectionIndex, setValue, updateSectionByElementId]);

  const removeCustomField = useCallback(
    async (elementId: string) => {
      const index = fields.findIndex((f) => f.elementId === elementId);
      remove(index);
    },
    [fields, remove]
  );

  const isValid = !errors || Object.keys(errors).length === 0;

  // This callback will dynamically adjust the layout based on the height of the widgets
  const childNodeHeightCallback = useCallback((elementId: string, height: number) => {
    // console.log("Sub Component reported height: ", height, " with elementId: ", elementId);
    setLayout((prev) => ({
      ...prev,
      [elementId]: { ...prev[elementId], layout: { ...prev[elementId].layout, h: height } },
    }));
  }, []);

  const onSwap = useCallback(
    (layout: GridLayout.Layout[]) => {
      // console.log("Swapping fields");
      const sortedLayout = layout.filter((d) => d.i !== "new").sort((a, b) => a.y - b.y);
      const fieldsDict = Object.fromEntries(Object.entries(fields).map(([, v]) => [v.elementId, v]));
      const newFieldsLayout = sortedLayout.map((d) => d.i).map((id) => fieldsDict[id]);
      replace(newFieldsLayout);
      // updateSectionByElementId(elementId, { customFields: newFieldsLayout });
    },
    [fields, replace]
  );

  const [showPlaceholder, setShowPlaceholder] = useState(true);
  useEffect(() => {
    if (fields.length === 0) {
      setShowPlaceholder(true);
    } else {
      setShowPlaceholder(false);
    }
  }, [fields.length]);

  interface InlineCustomFieldCreation {
    idx: number;
    initialValues: Partial<CustomField>;
  }
  const [inlineCustomField, setInlineCustomField] = useState<InlineCustomFieldCreation>();

  const handleInsertCustomField = useCallback(
    (index: number, data: Partial<CustomField>) => {
      insert(index, customFieldToCustomFieldForm(data));
    },
    [insert]
  );

  const onClear = useCallback(() => {
    setInlineCustomField(undefined);
  }, []);

  const onDrop = useCallback(
    async (layout: GridLayout.Layout[], item: GridLayout.Layout, e: MouseEvent) => {
      const sortedLayout = layout.sort((a, b) => a.y - b.y);
      const idx = sortedLayout.indexOf(item);
      // Now we get the custom properties from our dragData dataTransfer
      const dragContent = (e as any).dataTransfer.getData("dragData");
      if (!dragContent) return;
      const dragData = JSON.parse((e as any).dataTransfer.getData("dragData")) as Partial<CustomField>;
      if (idx !== -1) {
        if (!dragData.id) {
          setInlineCustomField((prev) => {
            if (!prev) return { idx, initialValues: dragData };
            return prev;
          });
        } else {
          const elem = customFieldToCustomFieldForm(dragData);
          insert(idx, elem);
        }
      }
    },
    [insert]
  );

  const isHeadless = useWatch({ control: control, name: `sections.${sectionIndex}.isHeadless` });

  return (
    <GridLayoutElementWrapper
      head={
        <div className="flex col-nowrap gap-5" style={{ width: "100%", height: "fit-content" }}>
          <div className="flex row-nowrap align-center gap-5" style={{ width: "100%", height: "fit-content" }}>
            <LucideIcon name={"panel-top-dashed"} style={{ width: "24px", height: "24px" }} />
            <div className="flex row-nowrap align-center gap-5">
              {!!isHeadless ? (
                <div className="label label-soft-info">Section header hidden by settings</div>
              ) : (
                <InputFormFieldControlled
                  id={`sections.${sectionIndex}.name`}
                  label=""
                  control={control}
                  placeholder={"Enter section name"}
                  disabled={disabled || !!isHeadless}
                  inputProps={{
                    onMouseDown: (e) => {
                      // We prevent dragging inside the actual widget
                      e.stopPropagation();
                    },
                  }}
                  horizontal
                  uncontained
                />
              )}
            </div>
            <div className="flex row-nowrap align-center gap-5" style={{ marginLeft: "auto" }}>
              <div className="flex row-nowrap align-center gap-5">
                {!isValid && (
                  <label className="label label-soft-danger" style={{ margin: 0 }}>
                    Validation error
                  </label>
                )}
              </div>
              {!disabledRemove && (
                <button
                  type="button"
                  className="btn btn-soft-danger"
                  title="Delete this section"
                  onMouseDown={(e) => {
                    // We prevent dragging inside the actual widget
                    e.stopPropagation();
                  }}
                  onClick={() => removeSection(elementId)}
                >
                  <LucideIcon name="x" />
                </button>
              )}
            </div>
          </div>
          {errors && <NestedErrorContainer errors={errors as any} />}
        </div>
      }
      ref={ref}
      isFolded={false}
      isValid={isValid}
    >
      <div className={styles.sectionMainContainer}>
        <div className={styles.sectionFormContainer}>
          <fieldset>
            <legend>Settings</legend>
          </fieldset>

          <ToggleFormField
            id={`sections.${sectionIndex}.isHeadless`}
            label={"Hide section header"}
            control={control}
            disabled={disabled}
            horizontal
            controllerFieldOverride={(field) => {
              const { value } = field;
              if (value === true) {
                setValue(`sections.${sectionIndex}.isFolded`, false);
                setValue(`sections.${sectionIndex}.name`, null);
              }
              return field;
            }}
          >
            <OverlayInfo icon="info">Hide section header in detail views.</OverlayInfo>
          </ToggleFormField>

          <div title={!!isHeadless ? "Section header is hidden" : undefined}>
            <ToggleFormField
              id={`sections.${sectionIndex}.isFolded`}
              label={"Fold section by default"}
              control={control}
              disabled={disabled || !!isHeadless}
              readOnly={disabled || !!isHeadless}
              horizontal
            >
              <OverlayInfo icon="info">Fold section by default in detail views.</OverlayInfo>
            </ToggleFormField>
          </div>

          {inlineCustomField && (
            <CustomFieldFormCreateModal
              index={inlineCustomField.idx}
              initialValues={inlineCustomField.initialValues}
              onSuccess={handleInsertCustomField}
              onClear={onClear}
            />
          )}
          <fieldset>
            <legend>Custom fields</legend>
          </fieldset>
          <div className={styles.rglMainContainerSection} ref={internalRef}>
            {!!width && (
              <div style={{ flex: "1 0 100%", position: "relative" }}>
                <GridLayout
                  className={styles.rglContainerSection}
                  layout={Object.values(layout).map((l) => l.layout)}
                  cols={1}
                  rowHeight={1}
                  width={width}
                  verticalCompact={true}
                  // margin={[0, 0]}
                  containerPadding={[5, 7]}
                  compactType={"vertical"}
                  preventCollision={false}
                  onDragStop={onSwap}
                  onDrop={onDrop}
                  isDroppable={true}
                  isDraggable={!disabled}
                  isResizable={false}
                  isBounded={true}
                  droppingItem={{ i: "new", w: 1, h: 7 }}
                >
                  {Object.entries(layout).map(([elementId, l]) => (
                    <div key={elementId} className="customField" data-grid={l.layout}>
                      <RenderCustomTypeSectionCustomField
                        elementId={elementId}
                        customField={l.field as Partial<CustomField>}
                        nodeHeightCallback={childNodeHeightCallback}
                        removeCustomField={removeCustomField}
                        canEdit={true}
                      />
                    </div>
                  ))}
                </GridLayout>
                <div
                  className={`flex col-nowrap align-center justify-center ${styles.placeholderOverlay}`}
                  style={{ display: showPlaceholder ? "flex" : "none" }}
                  onDragOver={() => setShowPlaceholder(false)}
                >
                  <LucideIcon name="hand" color={"var(--gray-400)"} style={{ width: "30px", height: "30px" }} />
                  <span style={{ color: "var(--gray-400)", fontWeight: 500 }}>
                    Drag & drop custom fields from the sidebar
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </GridLayoutElementWrapper>
  );
};
